import React, { useEffect, useState } from 'react';
import {
  FaApple,
  FaCarSide,
  FaClock,
  FaGooglePlay,
  FaMapMarkerAlt,
  FaPhoneAlt,
  FaSyncAlt,
  FaTimes,
} from 'react-icons/fa';
import {
  createPayment,
  verifyPayment,
  loadRazorpayScript,
} from './services/paymentService';
import {
  createCabComplaint,
  createCabDispute,
  getBookingDetails,
  getMyBookings,
} from './services/bookingService';
import './ProfileBookingHistory.css';

const BOOKINGS_PER_PAGE = 6;
const TRIP_FILTERS = [
  { value: 'all', label: 'All trips' },
  { value: 'one_way', label: 'One way' },
  { value: 'round_trip', label: 'Round trip' },
  { value: 'rental', label: 'Rental' },
];
const GOOGLE_PLAY_URL = 'https://play.google.com/store/apps/details?id=com.himangshu101.jayrajtravel';
const APP_STORE_URL = 'https://apps.apple.com/us/app/jayraj-travel-hotel-booking/id6759034839';

const formatDate = (value) => {
  if (!value) return 'Date not available';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Date not available';

  return new Intl.DateTimeFormat('en-IN', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
};

const formatAmount = (value) =>
  `₹${Number(value || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

const readableLabel = (value) =>
  value ? value.replace(/_/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase()) : 'Not available';

const getTripType = (booking) => {
  const tripType = booking.trip_type?.toLowerCase();
  const rideMode = booking.ride_mode?.toLowerCase();

  if (tripType === 'round_trip' || rideMode === 'round_trip') return 'round_trip';
  if (tripType === 'rental' || rideMode === 'rental') return 'rental';
  if (tripType === 'one_way') return 'one_way';
  if (rideMode === 'one_way' || rideMode === 'outstation') return 'one_way';
  return tripType || rideMode || '';
};

const getStatusClass = (status) => (status || 'unknown').toLowerCase().replace(/[^a-z0-9_-]/g, '');
const isRentalRide = (booking) => booking?.ride_mode?.toLowerCase() === 'rental';
const isPayAfterRide = (booking) =>
  isRentalRide(booking) ||
  (booking?.trip_type?.toLowerCase() === 'one_way' &&
    booking?.ride_mode?.toLowerCase() === 'normal');
const isRemainingPaymentDue = (booking) => {
  const status = booking?.status?.toLowerCase().replace(/[-\s]+/g, '_');
  const paymentStatus = booking?.payment_status?.toLowerCase();
  return status === 'ride_completed' && paymentStatus !== 'paid';
};
const hasPickupOtp = (booking) =>
  booking?.status?.toLowerCase() === 'assigned' &&
  booking.otp_pin !== null &&
  booking.otp_pin !== undefined &&
  String(booking.otp_pin).length > 0;

const mergeBookingDetails = (booking, latestDetails) => ({
  ...booking,
  ...latestDetails,
});

function ProfileBookingHistory() {
  const [bookings, setBookings] = useState([]);
  const [tripFilter, setTripFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [listError, setListError] = useState('');
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [isDetailsLoading, setIsDetailsLoading] = useState(false);
  const [detailsError, setDetailsError] = useState('');
  const [refreshingBookingId, setRefreshingBookingId] = useState(null);
  const [refreshErrors, setRefreshErrors] = useState({});
  const [payingBookingId, setPayingBookingId] = useState(null);
  const [paymentErrors, setPaymentErrors] = useState({});
  const [feedbackForm, setFeedbackForm] = useState(null);
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);
  const [feedbackError, setFeedbackError] = useState('');
  const [feedbackMessages, setFeedbackMessages] = useState({});

  useEffect(() => {
    let isCurrent = true;

    const loadBookings = async () => {
      if (!localStorage.getItem('accessToken')) {
        setListError('Please sign in to view your cab bookings.');
        setIsLoading(false);
        return;
      }

      try {
        const response = await getMyBookings();
        if (isCurrent) {
          setBookings(Array.isArray(response?.data) ? response.data : []);
        }
      } catch (error) {
        if (isCurrent) {
          setListError(
            error.response?.data?.message || 'We could not load your cab bookings. Please try again.'
          );
        }
      } finally {
        if (isCurrent) setIsLoading(false);
      }
    };

    loadBookings();
    return () => {
      isCurrent = false;
    };
  }, []);

  const showBookingDetails = async (bookingId) => {
    setSelectedBooking(null);
    setDetailsError('');
    setIsDetailsLoading(true);

    try {
      const response = await getBookingDetails(bookingId);
      if (!response?.data) {
        setDetailsError('Booking details were not returned by the server.');
        return;
      }
      setBookings((currentBookings) => currentBookings.map((booking) =>
        booking._id === bookingId ? mergeBookingDetails(booking, response.data) : booking
      ));
      setSelectedBooking(response.data);
    } catch (error) {
      setDetailsError(
        error.response?.data?.message || 'We could not load this booking’s details. Please try again.'
      );
    } finally {
      setIsDetailsLoading(false);
    }
  };

  const refreshBooking = async (bookingId) => {
    setRefreshingBookingId(bookingId);
    setRefreshErrors((currentErrors) => ({ ...currentErrors, [bookingId]: '' }));

    try {
      const response = await getBookingDetails(bookingId);
      if (!response?.data) {
        throw new Error('Booking details were not returned by the server.');
      }

      setBookings((currentBookings) => currentBookings.map((booking) =>
        booking._id === bookingId ? mergeBookingDetails(booking, response.data) : booking
      ));
    } catch (error) {
      setRefreshErrors((currentErrors) => ({
        ...currentErrors,
        [bookingId]: error.response?.data?.message || error.message || 'Could not refresh this booking. Please try again.',
      }));
    } finally {
      setRefreshingBookingId(null);
    }
  };

  const openFeedbackForm = (booking, kind) => {
    setFeedbackForm({
      bookingId: booking._id,
      kind,
      type: kind === 'complaint' ? 'driver_behavior' : 'fare_issue',
      subject: '',
      description: '',
      attachment: '',
    });
    setFeedbackError('');
  };

  const closeFeedbackForm = () => {
    if (feedbackSubmitting) return;
    setFeedbackForm(null);
    setFeedbackError('');
  };

  const submitFeedback = async (event) => {
    event.preventDefault();
    if (!feedbackForm || feedbackSubmitting) return;

    setFeedbackSubmitting(true);
    setFeedbackError('');
    const { bookingId, kind, type, subject, description, attachment } = feedbackForm;
    const payload = {
      booking_id: bookingId,
      [kind === 'complaint' ? 'complaint_type' : 'dispute_type']: type.trim(),
      subject: subject.trim(),
      description: description.trim(),
      ...(attachment.trim() ? { attachment: attachment.trim() } : {}),
    };

    try {
      const response = kind === 'complaint'
        ? await createCabComplaint(payload)
        : await createCabDispute(payload);
      if (response?.success === false) {
        throw new Error(response.message || `Could not submit the ${kind}.`);
      }
      setFeedbackMessages((currentMessages) => ({
        ...currentMessages,
        [`${bookingId}-${kind}`]: response?.message || `${kind === 'complaint' ? 'Complaint' : 'Dispute'} submitted successfully.`,
      }));
      setFeedbackForm(null);
    } catch (error) {
      setFeedbackError(
        error.response?.data?.message || error.message || `Could not submit the ${kind}. Please try again.`
      );
    } finally {
      setFeedbackSubmitting(false);
    }
  };

  const payRemainingAmount = async (booking) => {
    const bookingId = booking._id;
    setPaymentErrors((currentErrors) => ({ ...currentErrors, [bookingId]: '' }));

    if (!bookingId) {
      setPaymentErrors((currentErrors) => ({
        ...currentErrors,
        [bookingId]: 'Booking ID is unavailable. Please refresh and try again.',
      }));
      return;
    }

    setPayingBookingId(bookingId);
    try {
      const paymentResponse = await createPayment(bookingId);
      const paymentData = paymentResponse?.data?.data || paymentResponse?.data;
      if (!paymentData?.razorpay_order_id) {
        throw new Error('Payment order could not be created.');
      }

      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) throw new Error('Failed to load Razorpay. Please try again.');

      await new Promise((resolve, reject) => {
        const razorpay = new window.Razorpay({
          key: paymentData.razorpay_key_id,
          amount: paymentData.amount,
          currency: paymentData.currency || 'INR',
          name: 'Jayraj cabs',
          description: `Remaining payment for booking ${bookingId}`,
          order_id: paymentData.razorpay_order_id,
          theme: { color: '#A67C00' },
          handler: async (response) => {
            try {
              const verification = await verifyPayment({
                booking_id: bookingId,
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
              });
              if (verification?.success === false) {
                throw new Error(verification.message || 'Payment verification failed.');
              }
              resolve(verification);
            } catch (error) {
              reject(error);
            }
          },
          modal: { ondismiss: () => reject(new Error('Payment cancelled.')) },
        });
        razorpay.open();
      });

      const remainingAmount = Number(booking.remaining_amount);
      const updatedBooking = {
        ...booking,
        payment_status: 'paid',
        paid_amount: Number(booking.paid_amount || 0) +
          (Number.isFinite(remainingAmount) ? remainingAmount : 0),
        remaining_amount: 0,
      };
      setBookings((currentBookings) => currentBookings.map((currentBooking) =>
        currentBooking._id === bookingId
          ? mergeBookingDetails(currentBooking, updatedBooking)
          : currentBooking
      ));
      setSelectedBooking((currentBooking) =>
        currentBooking?._id === bookingId
          ? mergeBookingDetails(currentBooking, updatedBooking)
          : currentBooking
      );
      setPaymentErrors((currentErrors) => ({ ...currentErrors, [bookingId]: '' }));
    } catch (error) {
      console.error(error);
      setPaymentErrors((currentErrors) => ({
        ...currentErrors,
        [bookingId]: error.response?.data?.message || error.message || 'Payment failed. Please try again.',
      }));
    } finally {
      setPayingBookingId(null);
    }
  };

  const closeDetails = () => {
    setSelectedBooking(null);
    setDetailsError('');
  };

  const filteredBookings = bookings.filter(
    (booking) => tripFilter === 'all' || getTripType(booking) === tripFilter
  );
  const totalPages = Math.ceil(filteredBookings.length / BOOKINGS_PER_PAGE);
  const visibleBookings = filteredBookings.slice(
    (currentPage - 1) * BOOKINGS_PER_PAGE,
    currentPage * BOOKINGS_PER_PAGE
  );

  const selectTripFilter = (filter) => {
    setTripFilter(filter);
    setCurrentPage(1);
  };

  return (
    <section className="cab-history">
      <header className="cab-history__header">
        <div>
          <p className="cab-history__eyebrow">Your journeys</p>
          <h1>Cab booking history</h1>
          <p>Review your upcoming and past cab bookings, trip information, and payments.</p>
        </div>
        <div className="cab-history__count">
          <FaCarSide aria-hidden="true" />
          <span>{bookings.length} {bookings.length === 1 ? 'booking' : 'bookings'}</span>
        </div>
      </header>

      <aside className="cab-history__app-promo">
        <div className="cab-history__app-promo-copy">
          <span className="cab-history__app-promo-icon"><FaMapMarkerAlt aria-hidden="true" /></span>
          <p><strong>Want to see your driver’s live location?</strong> Download the Jayraj app now.</p>
        </div>
        <div className="cab-history__app-links">
          <a href={GOOGLE_PLAY_URL} target="_blank" rel="noopener noreferrer">
            <FaGooglePlay aria-hidden="true" /> Google Play
          </a>
          <a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer">
            <FaApple aria-hidden="true" /> App Store
          </a>
        </div>
      </aside>

      <nav className="cab-history__filters" aria-label="Filter bookings by trip type">
        {TRIP_FILTERS.map((filter) => (
          <button
            className={tripFilter === filter.value ? 'is-active' : ''}
            type="button"
            key={filter.value}
            aria-pressed={tripFilter === filter.value}
            onClick={() => selectTripFilter(filter.value)}
          >
            {filter.label}
            {filter.value === 'all' && <span>{bookings.length}</span>}
            {filter.value !== 'all' && (
              <span>{bookings.filter((booking) => getTripType(booking) === filter.value).length}</span>
            )}
          </button>
        ))}
      </nav>

      {isLoading && <div className="cab-history__message">Loading your bookings…</div>}
      {!isLoading && listError && (
        <div className="cab-history__message cab-history__message--error" role="alert">{listError}</div>
      )}
      {!isLoading && !listError && bookings.length === 0 && (
        <div className="cab-history__empty">
          <FaCarSide aria-hidden="true" />
          <h2>No cab bookings yet</h2>
          <p>Your cab bookings will appear here once you make a reservation.</p>
        </div>
      )}

      {!isLoading && !listError && bookings.length > 0 && (
        filteredBookings.length === 0 ? (
          <div className="cab-history__empty cab-history__empty--filtered">
            <FaCarSide aria-hidden="true" />
            <h2>No {readableLabel(tripFilter).toLowerCase()} bookings</h2>
            <p>Try another trip type to see your bookings.</p>
          </div>
        ) : (
          <>
            <div className="cab-history__results">
              Showing {((currentPage - 1) * BOOKINGS_PER_PAGE) + 1}–
              {Math.min(currentPage * BOOKINGS_PER_PAGE, filteredBookings.length)} of {filteredBookings.length} bookings
            </div>
            <div className="cab-history__list">
          {visibleBookings.map((booking) => (
            <article className="cab-booking-card" key={booking._id}>
              <div className="cab-booking-card__top">
                <span className="cab-booking-card__driver-status">
                  <small>Driver status</small>
                  <strong className={`cab-booking-card__status cab-booking-card__status--${getStatusClass(booking.status)}`}>
                    {readableLabel(booking.status)}
                  </strong>
                </span>
                <span className="cab-booking-card__id">Booking <strong>#{booking._id}</strong></span>
              </div>

              <div className="cab-booking-card__route">
                <div className="cab-booking-card__location">
                  <FaMapMarkerAlt aria-hidden="true" />
                  <div>
                    <small>Pickup</small>
                    <strong>{booking.pickup_location_id?.location_name || 'Location to be confirmed'}</strong>
                  </div>
                </div>
                <div className="cab-booking-card__route-line" aria-hidden="true" />
                <div className="cab-booking-card__location">
                  <FaMapMarkerAlt aria-hidden="true" />
                  <div>
                    <small>Drop-off</small>
                    <strong>{booking.drop_location_id?.location_name || 'Not specified'}</strong>
                  </div>
                </div>
              </div>

              <div className="cab-booking-card__summary">
                <span><FaClock aria-hidden="true" /> {formatDate(booking.pickup_date)}</span>
                <span className="cab-booking-card__trip-type">{readableLabel(getTripType(booking))}</span>
                {!isPayAfterRide(booking) && <strong>{formatAmount(booking.total_amount)}</strong>}
              </div>

              {isPayAfterRide(booking) ? (
                <p className="cab-booking-card__rental-payment">
                  Payment is due after your ride is completed.
                </p>
              ) : null}

              {booking.driver && (
                <div className="cab-booking-card__assignment">
                  <div className="cab-booking-card__driver-avatar" aria-hidden="true">
                    {booking.driver.name?.charAt(0)?.toUpperCase() || 'D'}
                  </div>
                  <div className="cab-booking-card__assignment-info">
                    <small>Assigned driver</small>
                    <strong>{booking.driver.name || 'Driver assigned'}</strong>
                    {booking.driver.mobile && (
                      <a href={`tel:${booking.driver.mobile}`}>
                        <FaPhoneAlt aria-hidden="true" /> {booking.driver.mobile}
                      </a>
                    )}
                  </div>
                  {booking.driver.vehicles?.[0] && (
                    <div className="cab-booking-card__vehicle">
                      <FaCarSide aria-hidden="true" />
                      <span>
                        <strong>{booking.driver.vehicles[0].vehicle_name || 'Vehicle assigned'}</strong>
                        {booking.driver.vehicles[0].vehicle_registration_number && (
                          <small>{booking.driver.vehicles[0].vehicle_registration_number}</small>
                        )}
                      </span>
                    </div>
                  )}
                </div>
              )}

              {hasPickupOtp(booking) && (
                <div className="cab-booking-card__otp" aria-label={`Pickup OTP ${booking.otp_pin}`}>
                  <div>
                    <small>Ride-start OTP</small>
                    <p>Share this code with your driver when your ride starts.</p>
                  </div>
                  <strong>{booking.otp_pin}</strong>
                </div>
              )}

              {refreshErrors[booking._id] && (
                <p className="cab-booking-card__refresh-error" role="alert">{refreshErrors[booking._id]}</p>
              )}
              {paymentErrors[booking._id] && (
                <p className="cab-booking-card__refresh-error" role="alert">{paymentErrors[booking._id]}</p>
              )}

              <div className="cab-booking-card__bottom">
                {isRentalRide(booking) && booking.payment_status?.toLowerCase() !== 'paid' ? (
                  <span className="cab-booking-card__payment-hint">Pay after trip completion</span>
                ) : (
                  <span className="cab-booking-card__payment">
                    Payment
                    <strong className={`cab-booking-card__payment-status cab-booking-card__payment-status--${getStatusClass(booking.payment_status)}`}>
                      {readableLabel(booking.payment_status)}
                    </strong>
                  </span>
                )}
                <div className="cab-booking-card__actions">
                  {isRemainingPaymentDue(booking) && (
                    <button
                      className="cab-booking-card__pay"
                      type="button"
                      disabled={Boolean(payingBookingId)}
                      onClick={() => payRemainingAmount(booking)}
                    >
                      {payingBookingId === booking._id
                        ? 'Processing payment…'
                        : Number(booking.remaining_amount) > 0
                          ? `Pay remaining ${formatAmount(booking.remaining_amount)}`
                          : 'Pay remaining amount'}
                    </button>
                  )}
                  <button type="button" onClick={() => openFeedbackForm(booking, 'dispute')}>
                    Cab dispute
                  </button>
                  <button type="button" onClick={() => openFeedbackForm(booking, 'complaint')}>
                    Cab complaint
                  </button>
                  <button
                    className="cab-booking-card__refresh"
                    type="button"
                    disabled={refreshingBookingId === booking._id}
                    onClick={() => refreshBooking(booking._id)}
                  >
                    <FaSyncAlt aria-hidden="true" className={refreshingBookingId === booking._id ? 'is-spinning' : ''} />
                    {refreshingBookingId === booking._id ? 'Refreshing…' : 'Refresh status'}
                  </button>
                  <button type="button" onClick={() => showBookingDetails(booking._id)}>
                    View details
                  </button>
                </div>
              </div>
              {feedbackMessages[`${booking._id}-dispute`] && (
                <p className="cab-booking-card__feedback-message" role="status">
                  {feedbackMessages[`${booking._id}-dispute`]}
                </p>
              )}
              {feedbackMessages[`${booking._id}-complaint`] && (
                <p className="cab-booking-card__feedback-message" role="status">
                  {feedbackMessages[`${booking._id}-complaint`]}
                </p>
              )}
            </article>
          ))}
            </div>
            {totalPages > 1 && (
              <nav className="cab-history__pagination" aria-label="Booking history pages">
                <button
                  type="button"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                >
                  Previous
                </button>
                {Array.from({ length: totalPages }, (_, index) => index + 1).map((page) => (
                  <button
                    type="button"
                    key={page}
                    className={page === currentPage ? 'is-active' : ''}
                    aria-current={page === currentPage ? 'page' : undefined}
                    aria-label={`Page ${page}`}
                    onClick={() => setCurrentPage(page)}
                  >
                    {page}
                  </button>
                ))}
                <button
                  type="button"
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
                >
                  Next
                </button>
              </nav>
            )}
          </>
        )
      )}

      {(isDetailsLoading || detailsError || selectedBooking) && (
        <div className="cab-details-backdrop" role="presentation" onClick={closeDetails}>
          <section
            className="cab-details"
            role="dialog"
            aria-modal="true"
            aria-labelledby="cab-details-title"
            onClick={(event) => event.stopPropagation()}
          >
            <button className="cab-details__close" type="button" onClick={closeDetails} aria-label="Close booking details">
              <FaTimes aria-hidden="true" />
            </button>
            <div className="cab-details__header">
              <span className="cab-details__header-icon"><FaCarSide aria-hidden="true" /></span>
              <div>
                <p>JAYRAJ CAB BOOKING</p>
                <h2 id="cab-details-title">Booking details</h2>
              </div>
            </div>
            {isDetailsLoading && <p className="cab-history__message">Loading booking details…</p>}
            {detailsError && <p className="cab-history__message cab-history__message--error" role="alert">{detailsError}</p>}
            {selectedBooking && (
              <div className="cab-details__content">
                <p className="cab-details__package">
                  {selectedBooking.package_id?.package_name || 'Cab trip'}
                </p>
                <p className="cab-details__id">Booking #{selectedBooking._id}</p>
                <dl>
                  <div><dt>Driver status</dt><dd><span className={`cab-booking-card__status cab-booking-card__status--${getStatusClass(selectedBooking.status)}`}>{readableLabel(selectedBooking.status)}</span></dd></div>
                  <div><dt>Vehicle</dt><dd>{selectedBooking.vehicle_type_id?.vehicle_type || 'Not assigned'}</dd></div>
                  <div><dt>Trip type</dt><dd>{readableLabel(selectedBooking.trip_type || selectedBooking.ride_mode)}</dd></div>
                  <div><dt>Pickup</dt><dd>{selectedBooking.pickup_location_id?.location_name || 'Not specified'}</dd></div>
                  <div><dt>Drop-off</dt><dd>{selectedBooking.drop_location_id?.location_name || 'Not specified'}</dd></div>
                  <div><dt>Pickup date</dt><dd>{formatDate(selectedBooking.pickup_date)}</dd></div>
                  {isRentalRide(selectedBooking) ? (
                    <div className="cab-details__payment-notice">
                      <dt>Payment timing</dt>
                      <dd>Payment is due after your trip is completed.</dd>
                    </div>
                  ) : (
                    <>
                      <div><dt>Payment status</dt><dd><span className={`cab-booking-card__payment-status cab-booking-card__payment-status--${getStatusClass(selectedBooking.payment_status)}`}>{readableLabel(selectedBooking.payment_status)}</span></dd></div>
                      <div><dt>Payment method</dt><dd>{readableLabel(selectedBooking.payment_method)}</dd></div>
                      <div><dt>Total amount</dt><dd>{formatAmount(selectedBooking.total_amount)}</dd></div>
                      <div><dt>Advance paid</dt><dd>{formatAmount(selectedBooking.advance_amount)}</dd></div>
                      <div><dt>Paid</dt><dd>{formatAmount(selectedBooking.paid_amount)}</dd></div>
                      <div><dt>Remaining</dt><dd>{formatAmount(selectedBooking.remaining_amount)}</dd></div>
                    </>
                  )}
                </dl>
                {hasPickupOtp(selectedBooking) && (
                  <div className="cab-booking-card__otp cab-details__otp" aria-label={`Pickup OTP ${selectedBooking.otp_pin}`}>
                    <div>
                      <small>Ride-start OTP</small>
                      <p>Share this code with your driver when your ride starts.</p>
                    </div>
                    <strong>{selectedBooking.otp_pin}</strong>
                  </div>
                )}
                {selectedBooking.driver && (
                  <div className="cab-details__driver">
                    <div className="cab-booking-card__driver-avatar" aria-hidden="true">
                      {selectedBooking.driver.name?.charAt(0)?.toUpperCase() || 'D'}
                    </div>
                    <div className="cab-booking-card__assignment-info">
                      <small>Assigned driver</small>
                      <strong>{selectedBooking.driver.name || 'Driver assigned'}</strong>
                      {selectedBooking.driver.mobile && (
                        <a href={`tel:${selectedBooking.driver.mobile}`}>
                          <FaPhoneAlt aria-hidden="true" /> {selectedBooking.driver.mobile}
                        </a>
                      )}
                    </div>
                    {selectedBooking.driver.vehicles?.[0] && (
                      <div className="cab-booking-card__vehicle">
                        <FaCarSide aria-hidden="true" />
                        <span>
                          <strong>{selectedBooking.driver.vehicles[0].vehicle_name || 'Vehicle assigned'}</strong>
                          {selectedBooking.driver.vehicles[0].vehicle_registration_number && (
                            <small>{selectedBooking.driver.vehicles[0].vehicle_registration_number}</small>
                          )}
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

          </section>
        </div>
      )}

      {feedbackForm && (
        <div className="cab-details-backdrop" role="presentation" onClick={closeFeedbackForm}>
          <section
            className="cab-details cab-feedback-form"
            role="dialog"
            aria-modal="true"
            aria-labelledby="cab-feedback-title"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              className="cab-details__close"
              type="button"
              onClick={closeFeedbackForm}
              aria-label="Close feedback form"
              disabled={feedbackSubmitting}
            >
              <FaTimes aria-hidden="true" />
            </button>
            <div className="cab-details__header">
              <span className="cab-details__header-icon"><FaCarSide aria-hidden="true" /></span>
              <div>
                <p>BOOKING #{feedbackForm.bookingId}</p>
                <h2 id="cab-feedback-title">Submit cab {feedbackForm.kind}</h2>
              </div>
            </div>
            <form className="cab-feedback-form__fields" onSubmit={submitFeedback}>
              <label>
                {feedbackForm.kind === 'complaint' ? 'Complaint type' : 'Dispute type'}
                <input
                  required
                  value={feedbackForm.type}
                  onChange={(event) => setFeedbackForm((currentForm) => ({
                    ...currentForm,
                    type: event.target.value,
                  }))}
                  placeholder={feedbackForm.kind === 'complaint' ? 'e.g. driver_behavior' : 'e.g. fare_issue'}
                />
              </label>
              <label>
                Subject
                <input
                  required
                  value={feedbackForm.subject}
                  onChange={(event) => setFeedbackForm((currentForm) => ({
                    ...currentForm,
                    subject: event.target.value,
                  }))}
                  maxLength={200}
                />
              </label>
              <label>
                Description
                <textarea
                  required
                  rows={5}
                  value={feedbackForm.description}
                  onChange={(event) => setFeedbackForm((currentForm) => ({
                    ...currentForm,
                    description: event.target.value,
                  }))}
                />
              </label>
              <label>
                Attachment path (optional)
                <input
                  value={feedbackForm.attachment}
                  onChange={(event) => setFeedbackForm((currentForm) => ({
                    ...currentForm,
                    attachment: event.target.value,
                  }))}
                  placeholder="/uploads/..."
                />
              </label>
              {feedbackError && (
                <p className="cab-feedback-form__error" role="alert">{feedbackError}</p>
              )}
              <div className="cab-feedback-form__actions">
                <button type="button" onClick={closeFeedbackForm} disabled={feedbackSubmitting}>
                  Cancel
                </button>
                <button type="submit" disabled={feedbackSubmitting}>
                  {feedbackSubmitting ? 'Submitting…' : `Submit ${feedbackForm.kind}`}
                </button>
              </div>
            </form>
          </section>
        </div>
      )}
    </section>
  );
}

export default ProfileBookingHistory;
